// app/apps/campsite-explorer/components/NearbyCampsites.tsx
'use client';

import { withBase } from '../lib/brand';
import { stripHtml } from '../lib/richText';
import React, { useCallback, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { NearbyCampsite } from '../types';
import { useCaPreview } from '../lib/caPreview';

interface NearbyCampsitesProps {
  campsites: NearbyCampsite[];
  currentCampsiteName: string;
  defaultImage: string;
  defaultImages?: {
    beach: string;
    forest: string;
    mountain: string;
    lake: string;
    desert: string;
    default: string;
  };
}

// Helper to get default image based on features/name
function getDefaultImageByType(campsite: NearbyCampsite, index: number, defaultImages?: NearbyCampsitesProps['defaultImages']): string {
  if (!defaultImages) return '';
  
  const name = campsite.name?.toLowerCase() || '';
  const features = campsite.features?.map(f => f.desc).join(' ').toLowerCase() || '';
  const combined = name + ' ' + features;
  
  if (combined.includes('beach') || combined.includes('coast')) return defaultImages.beach;
  if (combined.includes('forest') || combined.includes('bush')) return defaultImages.forest;
  if (combined.includes('mountain') || combined.includes('alpine')) return defaultImages.mountain;
  if (combined.includes('lake') || combined.includes('river')) return defaultImages.lake;
  if (combined.includes('desert') || combined.includes('outback')) return defaultImages.desert;
  
  // Cycle through different defaults based on index
  const imageTypes = ['beach', 'forest', 'mountain', 'lake', 'desert'];
  const imageType = imageTypes[index % imageTypes.length];
  return defaultImages[imageType as keyof typeof defaultImages] || defaultImages.default;
}

// Normalize entity name for URL
const normalizeEntityName = (name: string): string => {
  return name
    .replace(/['''`]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase()
    .trim()
    .replace(/^-+|-+$/g, '');
};

export default function NearbyCampsites({
  campsites,
  currentCampsiteName,
  defaultImage,
  defaultImages,
}: NearbyCampsitesProps) {
  const router = useRouter();
  // Photos that failed to load. The ca_preview skin treats them like no photo
  // (a title band, not a stock picture); production still swaps in the default.
  const [failed, setFailed] = useState<Record<string, true>>({});
  const caPreview = useCaPreview();
  const resolveTargetLocation = useCallback(() => {
    return (
      campsites[0]?.addresses?.[0]?.town ||
      campsites[0]?.addresses?.[0]?.state ||
      currentCampsiteName ||
      'Australia'
    );
  }, [campsites, currentCampsiteName]);

  const handleViewAllNearby = useCallback(() => {
    const location = resolveTargetLocation();
    const radius = 100;

    if (typeof window !== 'undefined') {
      const event = new CustomEvent('global-location-search', {
        detail: { location, radius },
      });
      window.dispatchEvent(event);
    }

    router.push(
      withBase(`/results?location=${encodeURIComponent(location)}&radius=${radius}`)
    );
  }, [resolveTargetLocation, router]);
  
  if (campsites.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No nearby sites found</p>
      </div>
    );
  }

  // ca_preview: real photos only. Sites with a photo of their own get a
  // photo card; the rest are a plain list (name, place, distance), so the
  // section never fills with map tiles or stock pictures.
  const isRealSrcPv = (s?: string | null) => !!s && /[./]/.test(s);
  const pvRows = caPreview
    ? campsites.slice(0, 6).map((c) => {
        const src = isRealSrcPv(c.image_src) ? c.image_src! : isRealSrcPv(c.gallery_images?.[0]?.image_src) ? c.gallery_images![0].image_src! : '';
        const d = typeof c.distance_km === 'number' ? c.distance_km : parseFloat(String(c.distance_km || '0'));
        const town = c.addresses?.[0]?.town || '';
        const state = c.addresses?.[0]?.state || '';
        return {
          c,
          src: failed[String(c.id)] ? '' : src,
          dist: d > 0 ? (d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`) : '',
          loc: [town, state].filter(Boolean).join(', '),
          slug: normalizeEntityName(c.name || ''),
        };
      })
    : [];
  const pvPhotos = pvRows.filter((r) => r.src);
  const pvList = pvRows.filter((r) => !r.src);

  if (caPreview) {
    return (
      <div className="ca-near">
        {pvPhotos.length > 0 && (
          <ul className="ca-near__grid" role="list">
            {pvPhotos.map((r) => (
              <li key={r.c.id}>
                <Link href={withBase(`/camp-sites/${r.slug}`)} className="ca-near__card">
                  <span className="ca-near__img">
                    <Image
                      src={r.src}
                      alt={`${r.c.name}${r.loc ? ` — ${r.loc}` : ''}`}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      onError={() => setFailed((f) => ({ ...f, [String(r.c.id)]: true }))}
                    />
                  </span>
                  <span className="ca-near__meta">{[r.loc, r.dist && `${r.dist} away`].filter(Boolean).join(' · ')}</span>
                  <span className="ca-near__name">{r.c.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {pvList.length > 0 && (
          <ul className="ca-near__list" role="list">
            {pvList.map((r) => (
              <li key={r.c.id}>
                <Link href={withBase(`/camp-sites/${r.slug}`)} className="ca-near__row">
                  <span className="ca-near__rowmain">
                    <span className="ca-near__name">{r.c.name}</span>
                    {r.loc && <span className="ca-near__meta">{r.loc}</span>}
                  </span>
                  {r.dist && <span className="ca-near__dist">{r.dist}</span>}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className="ca-near__more">
          <button type="button" onClick={handleViewAllNearby} className="ca-pill-link">
            See all nearby campsites
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {campsites.slice(0, 6).map((campsite, index) => {
          // Determine image source — prefer the campsite's real photo.
          // Real image references contain '.' or '/' — the US (RIDB) feed
          // returns provenance junk like "RIDB feed (US)" in image_src for
          // photo-less sites, which must fall through to the defaults.
          const isRealSrc = (s?: string | null) => !!s && /[./]/.test(s);
          let imageSrc = defaultImage;
          let hasPhoto = true;
          if (isRealSrc(campsite.image_src)) {
            imageSrc = campsite.image_src!;
          } else if (isRealSrc(campsite.gallery_images?.[0]?.image_src)) {
            imageSrc = campsite.gallery_images![0].image_src!;
          } else if (defaultImages) {
            imageSrc = getDefaultImageByType(campsite, index, defaultImages);
            hasPhoto = false;
          } else {
            hasPhoto = false;
          }
          if (failed[String(campsite.id)]) hasPhoto = false;

          const slug = normalizeEntityName(campsite.name || '');
          const distanceKm = typeof campsite.distance_km === 'number'
            ? campsite.distance_km
            : parseFloat(String(campsite.distance_km || '0'));
          const town = campsite.addresses?.[0]?.town || '';
          const state = campsite.addresses?.[0]?.state || 'Australia';
          const loc = town ? `${town}, ${state}` : state;
          // stripHtml: RIDB (US) descriptions arrive as HTML fragments.
          const blurb = stripHtml(campsite.short_desc || campsite.desc || '');

          return (
            <Link
              key={campsite.id}
              href={withBase(`/camp-sites/${slug}`)}
              className={`ca-nearby-card${hasPhoto ? '' : ' is-nophoto'} group block overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:shadow-lg`}
            >
              <div className="ca-nearby-card__media relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                <Image
                  src={imageSrc}
                  alt={`${campsite.name}${loc ? ` — ${loc}` : ''}`}
                  fill
                  className="object-cover transition duration-300 group-hover:scale-[1.04]"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  onError={(e) => {
                    setFailed((f) => ({ ...f, [String(campsite.id)]: true }));
                    (e.target as HTMLImageElement).src = defaultImage;
                  }}
                />
                {distanceKm > 0 && (
                  <span className="absolute left-3 top-3 z-[2] rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur">
                    {distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)} km`} away
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-[#1558b0]">{loc}</p>
                <h3 className="mt-1 text-base font-semibold leading-snug text-slate-900 group-hover:text-[#1558b0]">
                  {campsite.name}
                </h3>
                {blurb && (
                  <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-slate-600">{blurb}</p>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {/* See all */}
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={handleViewAllNearby}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
        >
          See all nearby campsites
          <span aria-hidden>&rarr;</span>
        </button>
      </div>
    </div>
  );
}
